import { MetroStationHeader, PromoBanner } from '@metro/station-details'
import { useEffect } from 'react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import type { LineStatusEntry } from '../components/stationPage/fetchLineStatuses'
import type { LineView } from './buildLineView'
import { LineStationList } from './LineStationList'
import { LineStatusRow } from './LineStatusRow'
import type { LineViewActions } from './useLineViewActions'
import { useLineTitle } from './useLineTitle'
import styles from './LineDetailsPanel.module.css'

export interface LineDetailsPanelProps {
  readonly view: LineView
  /** `undefined` hides the status row (loading, or no live status in this city). */
  readonly status: LineStatusEntry | null | undefined
  readonly actions: LineViewActions
}

/**
 * The line view's content — desktop side panel (Figma B, `9241:17331`) and
 * the phone sheet body (D, `9242:17014`): line header (ring in the line
 * colour, "<Name> line", "N stations", zones), live status, the "get the
 * app" banner, and the rail-diagram station list. Esc closes the line view
 * (legend B·6).
 */
export function LineDetailsPanel({
  view,
  status,
  actions,
}: LineDetailsPanelProps): React.ReactElement {
  const { t } = useTranslation('line-details')
  const title = useLineTitle(view)
  const { onClose } = actions
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])
  const zoneLabel = t('zone_label', { count: view.zoneCount })
  return (
    <div className={styles.panel} data-testid="line-details-panel" data-line={view.lineId}>
      <div className={styles.header}>
        <MetroStationHeader
          title={title}
          subtitle={t('stations_count', { count: view.stationCount })}
          badge={view.zones}
          lineHexColor={view.color}
          zoneLabel={zoneLabel}
          zoneAria={(zones) => `${zones} ${zoneLabel}`}
        />
      </div>
      {status !== undefined && <LineStatusRow status={status} onExpand={actions.onStatusExpand} />}
      {actions.ctaHref !== null && (
        <PromoBanner
          title={t('cta_title', { line: title })}
          description={t('cta_body')}
          cta={t('cta_button')}
          href={actions.ctaHref}
          onTap={actions.onCtaTap}
          testId="line-details-app-cta"
        />
      )}
      <LineStationList view={view} onStationTap={actions.onStationTap} />
    </div>
  )
}
